import type { ReactNode } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Check, Filter, Settings } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { IndexPagination } from '../index-page/IndexPagination';
import { IndexSelectionBar } from '../index-page/IndexSelectionBar';
import { CreateButton } from '../CreateButton';
import { SearchGroup, TOOLBAR_SEARCH_WIDTH } from '../index-page/SearchGroup';
import { Menu, type MenuItem } from '../Menu';
import { Tooltip } from '../Tooltip';
import { CountBadge } from '../CountBadge';
import { EmptyState } from '../EmptyState';
import type { ModalSize } from '../Modal';
import { DataTable, type DataColumn } from './DataTable';
import { RowPreviewModal } from './RowPreviewModal';
import { IndexListView } from '../index-page/IndexListView';
import { useIndexViewType } from '../index-page/useIndexViewType';
import { buildAuditColumns } from '../table/auditColumns';
import type { OtherInfoData } from '../table/OtherInfoModal';
import type { DataTableState } from './useDataTable';

/** One frozen empty set, so a cleared selection is referentially stable across renders. */
const EMPTY_SELECTION: ReadonlySet<string> = new Set();

/** Once per session is enough — the point is to be noticed, not to fill the console. */
let warnedAboutActionsColumn = false;
let warnedAboutBothAnchors = false;

/**
 * The whole standard table contract in one component: **search, sort, resize, page**.
 *
 * Use it for any table of records that is not an index page. Reach for `<DataTable>` alone
 * only when a surface genuinely has none of the other three — which in practice means a
 * fixed, complete, unsortable list, and there are very few of those.
 *
 * All four are server-side, driven by `useDataTable` through the same `ListQuery` every list
 * uses. Nothing here filters or sorts the page already in memory: that quietly applies to
 * the rows on screen and not the ones behind them, which looks like a feature and is a bug.
 */
export function DataTablePanel<T>({
  id,
  rowPreview,
  onRowOpen,
  openPermission,
  state,
  columns,
  rowKey,
  searchPlaceholder,
  onCreate,
  createLabel,
  createPermission,
  createDisabledReason,
  emptyMessage = 'Nothing to show.',
  emptySearchMessage,
  minWidth,
  fill = false,
  searchable = true,
  rowTone,
  groupRow,
  rowQuickActions,
  filters,
  settingsItems = [],
  selectable = true,
  numbered = true,
  bulkActions,
  onSelectionChange,
  otherInfo,
  auditColumns = true,
  rowActions,
  onEdit,
  editPermission,
  onDelete,
  deletePermission,
  deleteLabel,
  deleteDisabledReason,
}: {
  /** DOM id prefix, and the key column widths persist under. */
  id: string;
  state: DataTableState<T>;
  columns: DataColumn<T>[];
  rowKey: (row: T) => string;
  searchPlaceholder: string;

  /**
   * Adds a record to this table, in the toolbar's leading slot.
   *
   * **A CALLBACK, exactly as `<IndexPage>` takes one** — the panel draws the standard
   * `<CreateButton>` itself, with the same glyph, the same variant, the same gate and the same
   * disabled-reason tooltip an index page gets.
   *
   * It was typed `ReactNode` and that was the defect: fifteen callers each hand-rolled
   * `PermissionGate → Tooltip → Button → Plus`, which is fifteen chances to get one of the
   * four wrong, and four of them passed `<Tooltip block>` — a `block w-full` anchor that takes
   * the whole toolbar row and squeezes the search group beside it to nothing. A slot that
   * accepts arbitrary markup is a slot that invites a second Create button; this one names the
   * ACTION and lets the shell own how it looks.
   */
  onCreate?: () => void;
  /** The button's words, where a bare `Create` would name the wrong noun — `File a PPA`. */
  createLabel?: string;
  /** Hides the button entirely without it. */
  createPermission?: string;
  /** Disables it and says why — state, never permission. */
  createDisabledReason?: string | null;
  emptyMessage?: ReactNode;
  /** Shown when a search matched nothing — a different fact from having no records. */
  emptySearchMessage?: ReactNode;
  minWidth?: number;
  /**
   * Fill the height on offer and anchor the pager to the bottom of it.
   *
   * For a table that IS the surface — a profile tab, a bleed panel. Leave it off for a table
   * inside a card, where the card is sized by its content and a stretched table would leave a
   * gap under a short one.
   */
  fill?: boolean;
  /**
   * FALSE for a table NESTED under a search that already covers it — the Stored in folder's
   * container groups, product call 08 Oct 2026: one search for the folder, not one per group.
   * The toolbar row goes (search, count, cog — the cog lives in the search group); the caller's
   * own search reaches the server through its `fetchPage`. A Create still draws. Selection and
   * the bulk bar are untouched. Leave it on everywhere else: a table of records owes a search.
   */
  searchable?: boolean;
  /**
   * A DATA-STATE TINT on a whole row — today only `'red'`, for a row that needs the reader's
   * attention (an evidence item not yet evaluated, 08 Oct 2026). The `<InlineRow className>`
   * precedent, on the read-only tables. ONE tone or the other, never both in one class string:
   * a tinted row SWAPS the hover wash for its own deeper step, and a selected row keeps the
   * selection wash. Omitted, every row is exactly what it was.
   */
  rowTone?: (row: T) => 'red' | null;
  /**
   * A GROUP HEADER ROW before each run of rows sharing a key — the evidence Stored in folder's
   * containers (08 Oct 2026). Full width, drawn whenever the key changes from the row above, so the
   * ROWS must arrive ordered by it (the server sorts; a column sort would split the groups). A page
   * that starts mid-group repeats its header. Table view only — the list view draws no headers.
   */
  groupRow?: { key: (row: T) => string; render: (row: T) => ReactNode };
  /** Renders the funnel. Omitted, no filter control is drawn — a dead one is worse than none. */
  filters?: {
    open: boolean;
    onToggle: () => void;
    activeCount: number;
  };
  /**
   * Entries for the settings cog. Empty, no cog is drawn.
   *
   * The panel's own view options live here for the same reason the index page's do: a loose
   * checkbox floating over a table is a control with no home, and the strip is where a reader
   * already looks for what is narrowing the list.
   */
  settingsItems?: MenuItem[];
  /**
   * What can be done to a selection. Rendered in the selection bar's menu, exactly as the
   * index page renders its own — an embedded table is still a table of records.
   *
   * A panel with none still shows the checkbox: the menu renders disabled with a stated
   * reason, which is the disable-for-state rule. Selecting rows and finding nothing to do is
   * a smaller failure than not being able to select them at all.
   */
  bulkActions?: (rows: T[]) => MenuItem[];
  /**
   * The select gutter, ON by default — an embedded table reads like the index table unless
   * there is a reason for it not to.
   *
   * Turn it off ONLY for a table nothing can ever be done to: a read-only log, a preview.
   * Not for a table whose bulk actions are merely unbuilt — that is a gap to fill, and
   * `selectable={false}` written for that reason is how three profile panels came to have no
   * checkbox at all while every row in them could be deleted one at a time.
   */
  selectable?: boolean;
  /** The `#` gutter, ON by default. Off for a table where a row's position means nothing. */
  numbered?: boolean;
  /**
   * Makes the first data column open the row as a key/value preview.
   *
   * `{ title }` rather than a bare boolean, because a modal needs a heading and the panel
   * cannot invent one — "Row" is not a word about the record. Its ABSENCE leaves the first
   * column plain text, which is what makes a table read-only.
   *
   * The preview is built from the same `columns` the table renders, so the two cannot
   * disagree about what a row holds or what a value looks like.
   *
   * `details` appends the row's CHILD COLLECTIONS beneath those rows — the one thing a column
   * cannot express, because the most a column can say about a list is how long it is. It never
   * replaces a column, and a missing SCALAR is a missing column rather than a use for this.
   *
   * `size` matches the record's own editor, so reading a row and correcting it happen at one
   * width. Defaults to `lg`, which is what every caller took before the prop existed.
   */
  rowPreview?: { title: string; details?: (row: T) => ReactNode; size?: ModalSize };
  /**
   * Makes the first data column open the ROW itself — a navigation, not a preview.
   *
   * The pair `<IndexPage>` has carried all along (`onRowOpen` / `openPermission`), and its
   * absence here was a gap rather than a decision: a panel could open a key/value preview of
   * a row and had no way to open the row. The plan profile's department roster is the first
   * consumer — pressing a department drills into its PPAs, which is a navigation the preview
   * modal cannot be, because the editor behind it is itself a modal and this product does not
   * stack them.
   *
   * MUTUALLY EXCLUSIVE with `rowPreview`: both want the anchor, and a first column that did
   * two things depending on a prop nobody can see is worse than either. A development-time
   * error fires when both are supplied, and `onRowOpen` wins.
   *
   * `openPermission` is the key of what it OPENS, never the panel's own, and it hides the
   * anchor rather than disabling it — which leaves the column plain text, exactly as an
   * absent callback does.
   */
  onRowOpen?: (row: T) => void;
  openPermission?: string;
  /** Selection is the panel's state; this reports it out for a caller that acts on it. */
  onSelectionChange?: (rows: T[]) => void;
  /**
   * The row's audit metadata, which is what the list view's provenance line reads.
   *
   * Supplying it is what OFFERS the Table/List switch: the list row leads with who made the
   * record and when, so without it every row would open with a blank. A panel that cannot
   * answer that question stays a table rather than showing a list of half-rows.
   *
   * It may return NULL for a row that has no provenance to have — a DERIVED row, computed at
   * read time with no creator, no creation instant and no update trail. `<IndexPage>`'s own
   * `otherInfo` has allowed that since Stock Levels; this signature did not, which was a gap
   * rather than a decision. A panel returning null passes `auditColumns={false}` in the same
   * breath, or three columns of em dashes arrive instead.
   */
  otherInfo?: (row: T) => OtherInfoData | null;
  /**
   * Appends Created / Created by / Latest update from `otherInfo`. On by default.
   *
   * The one opt-out is a table whose OWN columns already are the creation metadata —
   * the same narrow exemption `<IndexPage>` documents. It also drops the list view's
   * provenance lead, or the actor and the instant print twice on one row.
   */
  auditColumns?: boolean;
  /**
   * What can be done to ONE row — the kebab, in the table's pinned trailing gutter and in the
   * list row's right-hand slot.
   *
   * A prop rather than a column the caller declares, and that is load-bearing twice over. An
   * `actions` column is a DATA column: the status hoist below moved status straight past it, so
   * the kebab rendered before status on every panel that flagged one; and `<IndexListView>`
   * builds its own menu from this prop, so in list view the entries were simply absent — Edit,
   * Delete and Restore gone, on five panels at once, silently. One prop, both surfaces,
   * nothing left for a call site to keep in step.
   */
  rowActions?: (row: T) => MenuItem[];
  /**
   * A row's most-used actions as icon buttons BEFORE the kebab — `<IndexTable>`'s `rowQuickActions`,
   * in a panel (09 Oct 2026). PROMOTED, never duplicated: the same `MenuItem`s, so permission and
   * disabled reason travel. Two or three; never destructive. The gutter grows a slot per entry.
   */
  rowQuickActions?: (row: T) => MenuItem[];
  /*
    The canonical Edit and Delete, in the order `<RowActionMenu>` fixes. Same names and
    meanings as the index shell's, so a reader moving between the two meets one menu.
  */
  onEdit?: (row: T) => void;
  editPermission?: string;
  onDelete?: (row: T) => void;
  deletePermission?: string;
  deleteLabel?: (row: T) => string;
  /** Disables Delete with a reason rather than hiding it — for state, not permission. */
  deleteDisabledReason?: (row: T) => string | null;
}) {
  const { hasPermission } = useAuth();

  const searching = state.search.trim().length > 0;

  /*
    A column keyed `actions` is the old shape arriving by copy-paste, and both of its failure
    modes are invisible in review — one moves a column, the other empties a menu on a view
    nobody had open. Caught in development rather than typed away: `DataColumn` is meant to be
    structurally a `ColumnDef`, so there is no field to remove that would make it
    unexpressible. The same idiom as `<StackedCell secondary>` and `<StatusText pending>`.
  */
  useEffect(() => {
    if (!import.meta.env.DEV || warnedAboutActionsColumn) return;
    if (!columns.some((column) => column.key === 'actions')) return;

    warnedAboutActionsColumn = true;
    console.error(
      `<DataTablePanel id="${id}"> declares an "actions" column. The kebab is the shell's ` +
        `trailing gutter — pass rowActions instead, or status is hoisted past it and the ` +
        `list view renders no actions at all. See CLAUDE.md, "An embedded table's row ` +
        `actions are the shell's gutter".`,
    );
  }, [columns, id]);

  /*
    Both want column 0, and a first column that opened a preview on one panel and navigated
    on another — with nothing on screen saying which — is worse than either. `onRowOpen`
    wins, so the preview modal is simply never reachable; the error names the panel.
  */
  useEffect(() => {
    if (!import.meta.env.DEV || warnedAboutBothAnchors) return;
    if (!rowPreview || !onRowOpen) return;

    warnedAboutBothAnchors = true;
    console.error(
      `<DataTablePanel id="${id}"> supplies both rowPreview and onRowOpen. They are mutually ` +
        `exclusive — both claim the row anchor — and onRowOpen wins, so the preview is ` +
        `unreachable. See CLAUDE.md, "An embedded table opens a row".`,
    );
  }, [id, onRowOpen, rowPreview]);

  /*
    STATUS IS ALWAYS THE LAST DATA COLUMN, exactly as `<IndexPage>` enforces it — one
    predictable position beats a per-table order, and a panel table is still a table of records.
    Declaration order is preserved for everything else.

    Done here rather than at the two render sites below, so the table and the list can never
    disagree about which column is the state: the list reads `column.status` to fill its
    right-hand stack, and the table reads the order.

    "Last DATA column" is only true because the kebab is a gutter `<DataTable>` draws after
    all of these. While it was a column a caller declared, this hoist jumped it and every
    panel rendered `… | ⋮ | Status`. Do not re-introduce one; `rowActions` is the way in, and
    the check above says so out loud.
  */
  /*
    Created / Created by / Latest update are appended HERE, from `otherInfo`, exactly as
    `<IndexPage>` appends them — `buildAuditColumns` is the one definition both shells read.

    It lived in the index shell alone, and the consequence was not that panels went without:
    all nine personnel section panels hand-rolled a `createdAt` column of their own, and not
    one of them had an update column. Updating a record showed nothing anywhere a reader
    looks — no column in the table, and `<RowPreviewModal>` is built from these same columns,
    so the detail modal could not show it either. The list view was the only surface that
    ever had it, because it reads `otherInfo` directly.

    They go before the status hoist, so the run stays `… | Created | Created by |
    Latest update | Stage | Status`, with the kebab gutter after all of them.

    THE STAGE SITS IMMEDIATELY BEFORE STATUS, which is `<IndexPage>`'s own arrangement — and
    the two shells resolve it in one place each for the same reason the status hoist is here:
    the table reads the ORDER and the list reads the FLAG, so a panel deciding it at its two
    render sites is how the two views come to disagree about which column is the state.
  */
  const ordered = useMemo(() => {
    const audit = auditColumns && otherInfo ? buildAuditColumns<T>(otherInfo) : [];

    return [
      ...columns.filter((c) => !c.status && !c.stage),
      ...audit,
      ...columns.filter((c) => c.stage),
      ...columns.filter((c) => c.status),
    ];
  }, [columns, auditColumns, otherInfo]);

  /*
    Defaults to 'table' rather than the index page's 'list': this component is what a page
    reaches for when it wants a table, so opening as a list would answer a question the caller
    already answered. The reader's own choice still wins and still persists per panel.
  */
  const { viewType, setViewType } = useIndexViewType(id, 'table');
  const asList = viewType === 'list' && otherInfo !== undefined;

  /*
    Selection lives here rather than in useDataTable: it is a property of the surface, and a
    caller that only wants to read a table should not have to thread state it never uses.
    Keyed by rowKey so it survives a re-fetch that returns new object identities.

    It carries the page and search it was made on.

    A page change clears it — carrying a selection across pages would let a bulk action run
    against rows the reader can no longer see, which is the one thing a selection must never
    do quietly. That is DERIVED here rather than reset in an effect: an effect would clear it
    one render after the page moved, so a bulk action fired in that gap would act on the old
    page's rows.
  */
  const [selection_, setSelection] = useState<{
    page: number;
    search: string;
    keys: ReadonlySet<string>;
  }>({ page: state.page, search: state.search, keys: EMPTY_SELECTION });

  const stale = selection_.page !== state.page || selection_.search !== state.search;

  /*
    A SELECTED ROW THAT LEFT THE PAGE IS NOT SELECTED. An action can remove the very rows it was
    run on — the evidence Received folder's Evaluate moves them out of the folder — and the bar
    then sat at "2 selected" over an empty table, holding keys of rows nobody can see (08 Oct
    2026). DERIVED against the rows on screen, like the page rule above: the old rows stay until
    the new ones land, so a reload or a sort never drops a selection that is still there.
  */
  const selectedKeys = useMemo(() => {
    const kept = stale ? EMPTY_SELECTION : selection_.keys;
    if (kept.size === 0) return kept;

    const onScreen = new Set(state.rows.map((row) => rowKey(row)));
    const live = [...kept].filter((key) => onScreen.has(key));

    return live.length === kept.size ? kept : new Set(live);
    // rowKey is a caller's pure function of the row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stale, selection_.keys, state.rows]);

  const setSelectedKeys = (next: (current: ReadonlySet<string>) => ReadonlySet<string>) =>
    setSelection({ page: state.page, search: state.search, keys: next(selectedKeys) });

  const selectedRows = state.rows.filter((row) => selectedKeys.has(rowKey(row)));

  useEffect(() => {
    onSelectionChange?.(selectedRows);
    // The rows are derived; the keys are what actually say the selection moved.
  }, [selectedKeys, state.rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const allSelected =
    state.rows.length > 0 && state.rows.every((row) => selectedKeys.has(rowKey(row)));
  const someSelected = state.rows.some((row) => selectedKeys.has(rowKey(row)));

  const selection = selectable
    ? {
        isSelected: (row: T) => selectedKeys.has(rowKey(row)),
        onToggle: (row: T) =>
          setSelectedKeys((current) => {
            const next = new Set(current);
            if (!next.delete(rowKey(row))) next.add(rowKey(row));
            return next;
          }),
        onToggleAll: () =>
          setSelectedKeys(() => (allSelected ? EMPTY_SELECTION : new Set(state.rows.map(rowKey)))),
        allSelected,
        someSelected,
      }
    : undefined;

  /*
    The cog, in SECTIONS, matching the index page's own — "View" over the two view entries,
    then the panel's entries under "Records".

    It was a flat run of Table / List / Show deleted, which put a view switch and a record
    filter in one undifferentiated list and left the ticked entry's label indented past the
    unticked ones. `<Menu>` draws the headings, the rules between sections and the fixed icon
    gutter, so the two cogs are one layout rather than two that happen to look similar.

    A caller's own entries default to "Records" rather than being forced into it: an entry that
    names its own section keeps it, which is what lets a panel add a third group later without
    this component learning about it.
  */
  const cogItems: MenuItem[] = [
    ...(otherInfo
      ? ([
          {
            key: 'view-table',
            label: 'Table',
            section: 'View',
            icon:
              viewType === 'table' ? (
                <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
              ) : undefined,
            onClick: () => setViewType('table'),
          },
          {
            key: 'view-list',
            label: 'List',
            section: 'View',
            icon:
              viewType === 'list' ? (
                <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
              ) : undefined,
            onClick: () => setViewType('list'),
          },
        ] satisfies MenuItem[])
      : []),
    ...settingsItems.map((item) => ({ ...item, section: item.section ?? 'Records' })),
  ];

  /*
    The row a reader has opened. A table row is a poor way to READ one record — the columns
    are spread across a scroll and the narrow ones truncate — so the first column opens the
    same row down the page instead, as a key/value table.

    Held here rather than by each panel: every table of records owes this, and six panels
    each holding their own state is six chances for one of them to skip it.
  */
  const [previewing, setPreviewing] = useState<T | null>(null);

  /*
    ONE anchor handler, resolved here so the table and the list view cannot disagree about
    what pressing the first column does — the same reason the status hoist and the column
    permission filter are resolved above the render sites rather than inside them.

    A gated navigation leaves the column PLAIN TEXT, which is exactly what an absent callback
    does: hide, never disable, and never a button that opens nothing.
  */
  const canOpenRow = onRowOpen && (!openPermission || hasPermission(openPermission));
  const anchorOpen = canOpenRow ? onRowOpen : rowPreview ? setPreviewing : undefined;

  /*
    NO PADDING HERE, and this is the second time that was decided.

    A `bleed` profile panel gives its child no inset, so a panel dropped straight into one sits
    with its search group's border ON the panel's border — one control that has come apart.
    The fix for that is a `p-4` WRAPPER at the call site, which is what `<PositionProfilePage>`
    and `<PlantillaPositionProfilePage>` already do.

    It was briefly fixed here instead, keyed on `fill` — and `fill` is the wrong question: it
    means "take the height you are given", which is also true of a table in a MODAL body and
    of the two pages above, all of which already have their own inset. Those three came out
    with 32px. The gap belongs to the container, and the container is the caller.
  */
  return (
    <div className={`flex min-w-0 flex-col gap-3 ${fill ? 'h-full min-h-0' : ''}`}>
      {/*
        Selecting rows swaps the strip for the selection bar — the SAME component and the same
        swap the index page makes, not a second arrangement. Mixing per-row and bulk
        affordances in one row is how a bulk action gets applied to the wrong set, and a reader
        who has learned the index page's behaviour must not have to learn a second one here.
      */}
      {selectedKeys.size > 0 ? (
        <IndexSelectionBar
          module={id}
          selectedCount={selectedKeys.size}
          bulkItems={bulkActions?.(selectedRows) ?? []}
          onClear={() => setSelectedKeys(() => EMPTY_SELECTION)}
        />
      ) : !searchable && !onCreate ? null : (
        <div className="flex min-w-0 shrink-0 items-center gap-2">
          {/*
            The SAME button the index toolbar draws, from the same component — so a panel and a
            page cannot end up with different Creates, and no call site can hand in markup that
            claims the row.
          */}
          {onCreate ? (
            <CreateButton
              id={`${id}-create`}
              onClick={onCreate}
              label={createLabel}
              permission={createPermission}
              disabledReason={createDisabledReason}
            />
          ) : null}
          {/*
          The same connected strip the index page carries — filter, search, count, settings —
          because an embedded table is still a table of records and a reader should not have to
          learn a second shape for it. The filter and the cog are SEGMENTS of the group, not
          buttons beside it, which is what makes it one control rather than three.

          Filter leads and the cog closes, matching the index toolbar: one narrows the list and
          belongs where the reader starts, the other configures the surface and belongs at the
          far end. Two surfaces disagreeing about that order is the drift this shared component
          exists to prevent.
        */}
          {searchable ? (
          <SearchGroup
            id={id}
            value={state.search}
            onChange={state.setSearch}
            placeholder={searchPlaceholder}
            total={state.total}
            loading={state.loading}

            // Fills whatever the leading slot leaves, and never more than half the row — the
            // same constant the index toolbar uses, imported rather than retyped.
            className={onCreate ? TOOLBAR_SEARCH_WIDTH : undefined}
            leading={
              filters ? (
                <Tooltip label={filters.open ? 'Hide filters' : 'Show filters'}>
                  <button
                    type="button"
                    id={`${id}-toggle-filters`}
                    onClick={filters.onToggle}
                    aria-pressed={filters.open}
                    aria-label={filters.open ? 'Hide filters' : 'Show filters'}
                    // Leads the group, so the radius is its and the divider is on its right.
                    className={[
                      'flex shrink-0 items-center gap-1.5 rounded-l-lg border-r border-slate-300 px-3 transition',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-200',
                      filters.open
                        ? 'bg-slate-100 text-slate-700'
                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700',
                    ].join(' ')}
                  >
                    <Filter className="h-4 w-4" aria-hidden="true" />
                    {filters.activeCount > 0 ? (
                      <CountBadge count={filters.activeCount} tone="blue" />
                    ) : null}
                  </button>
                </Tooltip>
              ) : null
            }
          >
            {cogItems.length > 0 ? (
              <Menu
                id={`${id}-settings-menu`}
                items={cogItems}
                trigger={({ open, toggle }) => (
                  <Tooltip label="Table settings">
                    <button
                      type="button"
                      id={`${id}-settings`}
                      onClick={toggle}
                      aria-haspopup="menu"
                      aria-expanded={open}
                      aria-label="Table settings"
                      className="flex h-full items-center rounded-r-lg border-l border-slate-300 px-3 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-200"
                    >
                      <Settings className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </Tooltip>
                )}
              />
            ) : null}
          </SearchGroup>
          ) : null}
        </div>
      )}

      {/*
        EMPTY is resolved ABOVE the two surfaces, exactly as `<IndexPage>` resolves it — so
        the table and the list cannot answer "this section is empty" differently.

        They did. `<DataTable>` handled `rows.length === 0` itself and `<IndexListView>` had
        no empty branch at all, so which of three things a reader met depended on the view
        their panel happened to be persisted on: a bordered message in table view, a
        select-all strip floating over nothing in list view, or — with selection off —
        nothing whatsoever under the toolbar. Three panels of one profile, three answers.

        `<DataTable>` keeps its own empty branch for callers that use it directly, without
        the panel around it; through the panel it is now unreachable.

        A SEARCH that matched nothing is a different fact from a section with no records,
        and the panel already made that distinction for one surface — it just made it in a
        place only one surface could read.
      */}
      {!state.loading && state.rows.length === 0 ? (
        <EmptyState
          message={
            searching ? (emptySearchMessage ?? "Nothing matches that search.") : emptyMessage
          }
        />
      ) : asList && otherInfo ? (
        <div
          className={`modern-scrollbar rounded-lg border border-slate-200 ${
            fill ? 'min-h-0 flex-1 overflow-auto' : 'overflow-x-auto'
          }`}
        >
          {/*
            The canonical list surface, not a second one. `DataColumn` is structurally a
            `ColumnDef` — same key, header, width, render — so the panel's columns feed it
            directly and the two views cannot disagree about what the record shows.
          */}
          <IndexListView<T>
            module={id}
            columns={ordered}
            rows={state.rows}
            rowKey={rowKey}
            firstRow={state.firstRow}
            loading={state.loading}
            otherInfo={otherInfo}
            /*
              One flag, both views — exactly as `<IndexPage>` wires it. A table whose own
              columns ARE the creation metadata is a list that must not lead with it either,
              or the actor and the instant print twice on the same row.
            */
            provenanceLead={auditColumns}
            rowActions={rowActions}
            rowQuickActions={rowQuickActions}
            onEdit={onEdit}
            editPermission={editPermission}
            onDelete={onDelete}
            deletePermission={deletePermission}
            deleteLabel={deleteLabel}
            deleteDisabledReason={deleteDisabledReason}
            /*
              The list anchor opens the same preview the table's does. Left unwired it was a
              plain label on one view and a button on the other, over identical rows.
            */
            onRowOpen={anchorOpen}
            selectable={selectable}
            selectedKeys={new Set(selectedKeys)}
            onToggleRow={(key) =>
              setSelectedKeys((current) => {
                const next = new Set(current);
                if (!next.delete(key)) next.add(key);
                return next;
              })
            }
            onToggleAll={selection?.onToggleAll}
            rowTone={rowTone}
          />
        </div>
      ) : (
        <DataTable
          id={id}
          columns={ordered}
          rows={state.rows}
          rowKey={rowKey}
          sort={state.sort}
          onToggleSort={state.toggleSort}
          loading={state.loading}
          minWidth={minWidth}
          fill={fill}
          numbered={numbered}
          selection={selection}
          rowActions={rowActions}
          rowQuickActions={rowQuickActions}
          onEdit={onEdit}
          editPermission={editPermission}
          onDelete={onDelete}
          deletePermission={deletePermission}
          deleteLabel={deleteLabel}
          deleteDisabledReason={deleteDisabledReason}
          otherInfo={otherInfo}
          /*
            The anchor exists only when the panel asked for a preview. A first column that
            looks clickable and opens nothing is the dead affordance the hide-rule forbids.
          */
          onRowOpen={anchorOpen}
          firstRow={state.firstRow}
          rowTone={rowTone}
          groupRow={groupRow}
          emptyMessage={
            searching ? (emptySearchMessage ?? 'Nothing matches that search.') : emptyMessage
          }
        />
      )}

      {rowPreview ? (
        <RowPreviewModal
          id={`${id}-preview`}
          open={previewing !== null}
          row={previewing}
          /*
            `ordered`, not the raw `columns`: the reader sees the preview in the order the
            table is showing, including anything the shell moved or they reordered.
          */
          columns={ordered}
          title={rowPreview.title}
          details={rowPreview.details}
          size={rowPreview.size}
          onClose={() => setPreviewing(null)}
        />
      ) : null}

      {/*
        Always rendered once there is anything to page: a list that silently caps at a page
        size is a list that lies about its size. `shrink-0` keeps it on the bottom edge when
        the panel fills — the table above absorbs the slack instead.
      */}
      {state.total > 0 ? (
        <IndexPagination
          module={id}
          page={state.page}
          pageCount={state.pageCount}
          firstRow={state.firstRow}
          lastRow={state.lastRow}
          total={state.total}
          loading={state.loading}
          onPageChange={state.setPage}
        />
      ) : null}
    </div>
  );
}
